import type { ReactNode } from "react";
import { useState, useCallback } from "react";

import { Oval8 } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { AddonsCartButtonTooltipFlexItem } from "app/components/AddonsCart/AddonsCartButton/AddonsCartButtonStyled";
import { useAddonsCart } from "app/components/hooks/useAddonsCart";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

interface IAddonsCartTooltipProps {
    children: ReactNode;
    parent: "button" | "banner";
}

export const AddonsCartTooltip = ({ children, parent }: IAddonsCartTooltipProps) => {
    const { totalCartItems, cartItemsText, addonsFromCookies } = useAddonsCart();
    const [isTooltipOpened, setIsTooltipOpened] = useState<boolean>(false);

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            setIsTooltipOpened(isOpen);
            if (isOpen) {
                const target = `cart ${parent === "button" ? "icon" : "banner"}`;

                allTrackers.dispatchTrackEvent("internal link", "hover", `${target}/add ons (${addonsFromCookies})`);
            }
        },
        [addonsFromCookies, parent],
    );

    return (
        <Tooltip
            onOpenChange={handleOpenChange}
            open={isTooltipOpened}
            content={
                totalCartItems > 0 ? (
                    <Stack>
                        <Text fontVariant="tooltip">{translate("addons.cart.tooltip.title")}</Text>
                        {cartItemsText.map((itemText) => (
                            <AddonsCartButtonTooltipFlexItem key={itemText}>
                                <Oval8
                                    size={3}
                                    color={colorsPalettes.carbon[0]}
                                />
                                <Text fontVariant="tooltip">{itemText}</Text>
                            </AddonsCartButtonTooltipFlexItem>
                        ))}
                    </Stack>
                ) : (
                    <Text fontVariant="tooltip">{translate("addons.cart.tooltip.text")}</Text>
                )
            }
            placement="bottom"
            delay={0}
        >
            {children}
        </Tooltip>
    );
};
